import { GetStaticProps } from 'next';

import {
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	PageInformationFieldsFragment,
	RoadMaintenanceOverviewSingleFieldsFragment
} from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetRoadMaintenanceOverview } from 'api/graphql-helper';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import { BaseModule } from 'modules/base-module/base-module';
import { RoadMaintenanceOverview } from 'modules/road-maintenance-overview';
import IntroHeader from 'modules/intro-header/intro-header';

import { initialState as settingsInitialState } from 'redux/stores/settings';
import { initialState as roadMaintenanceInitialState } from 'redux/stores/road-maintenance';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import { ROUTES } from 'constants/routes';
import { searchRoadMaintenance } from 'api/algolia';

interface IRoadMaintenanceOverviewPageProps extends IBasePageProps {
	roadMaintenanceOverview?: RoadMaintenanceOverviewSingleFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
}

const RoadMaintenanceOverviewPage = ({
	pageInformation,
	roadMaintenanceOverview
}: IRoadMaintenanceOverviewPageProps): React.JSX.Element => {
	const { header, list, modules } = { ...roadMaintenanceOverview };
	const seoModule = roadMaintenanceOverview?.seo;

	const headerDefaultOptions = {
		backgroundColor: header?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
		padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
	};

	return (
		<Layout navbar={pageInformation?.navbar} footer={pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={pageInformation?.seo} />
			<BaseModule defaultModuleOptions={headerDefaultOptions}>
				<IntroHeader module={header} slug={ROUTES.ROAD_MAINTENANCES.fullPath} />
			</BaseModule>
			<BaseModule
				defaultModuleOptions={{
					backgroundColor: 'grey',
					padding: 'none'
				}}
			>
				<RoadMaintenanceOverview module={list || undefined} />
			</BaseModule>
			<ModuleBuilder modules={modules || []} slug={ROUTES.ROAD_MAINTENANCES.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetRoadMaintenanceOverview({ variables: { locale, publicationState } }, client);
		const { pageInformation, roadMaintenanceOverview } = result.props.data;

		const { results: roadMaintenances, total: roadMaintenancesCount } = await searchRoadMaintenance('', {
			page: 1,
			limit: 9999,
			locale
		});

		if (roadMaintenanceOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				roadMaintenanceOverview: roadMaintenanceOverview.data?.attributes,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'road-maintenance-overview',
						contentTypeId: roadMaintenanceOverview?.data?.documentId
					},
					roadMaintenance: {
						...roadMaintenanceInitialState,
						roadMaintenances,
						roadMaintenancesCount
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in road maintenance overview', error);

		return {
			notFound: true
		};
	}
};

export default RoadMaintenanceOverviewPage;
